import { Box, Stack, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { ColoredAvatar } from 'src/components/ColoredAvatar/colored-avatar';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import {
  PillHoverCardInfoRow,
  PillHoverCardTitle,
} from 'src/pages/Conversations/SidePanel/ConversationDetails/properties/pill-hover-card';
import { useCopyToClipboard } from 'src/utils/hooks/use-copy-to-clipboard';
import { copyButtonSx, kickerSx, labelChipSx, noteSx } from './client-hover-card.styles';

export interface ClientHoverCardProps {
  name: string;
  kicker?: string;
  email?: string;
  phone?: string;
  labels?: string[];
  note?: string;
}

const InfoRow = ({ icon, value }: { icon: string; value: string }) => {
  const { copyToClipboard } = useCopyToClipboard();

  return (
    <PillHoverCardInfoRow
      icon={icon}
      action={
        <Box
          component="button"
          type="button"
          aria-label="Copy"
          onClick={() => copyToClipboard(value)}
          sx={copyButtonSx}
        >
          <DynamicIcon name="copy" library="lucide" size={12} />
        </Box>
      }
    >
      {value}
    </PillHoverCardInfoRow>
  );
};

export const ClientHoverCard = ({
  name,
  kicker = 'Client',
  email,
  phone,
  labels,
  note,
}: ClientHoverCardProps) => {
  return (
    <>
      <PillHoverCardTitle
        leading={
          <ColoredAvatar
            name={name}
            sx={{ width: '1.5rem', height: '1.5rem', flexShrink: 0 }}
          />
        }
        trailing={
          <Typography component="span" sx={kickerSx}>
            {kicker}
          </Typography>
        }
      >
        {name}
      </PillHoverCardTitle>

      {!!email && <InfoRow icon="mail" value={email} />}
      {!!phone && <InfoRow icon="phone" value={phone} />}

      {!!labels?.length && (
        <Stack direction="row" flexWrap="wrap" gap="0.25rem" sx={{ mt: '0.3125rem' }}>
          {labels.map((chipLabel) => (
            <Box key={chipLabel} component="span" sx={labelChipSx}>
              {chipLabel}
            </Box>
          ))}
        </Stack>
      )}

      {!!note && (
        <Typography
          sx={noteSx}
          // Client notes are stored as rich-text HTML with no plain-text counterpart, so a
          // React tree can't render them; DOMPurify.sanitize (already a dependency, same call
          // as policy-card.tsx) strips scripts/handlers before they reach the DOM.
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(note, { USE_PROFILES: { html: true } }),
          }}
        />
      )}
    </>
  );
};
